import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { SlidersHorizontal } from "lucide-react";
import { get } from "@/lib/api";
import { CATEGORY_ICONS } from "@/lib/data";
import { StudioCard } from "@/components/consumer/StudioCard";
import { SessionCard } from "@/components/consumer/SessionCard";

const REGIONS = ["Kadıköy", "Caddebostan", "Suadiye", "Erenköy", "Ataşehir", "Beşiktaş", "Nişantaşı"];
const TIMES = [{ v: "", l: "Tüm saatler" }, { v: "06:00", l: "Sabah" }, { v: "12:00", l: "Öğlen" }, { v: "17:00", l: "Akşam" }];

function buildDates() {
  const out = [{ v: "", l: "Tüm günler" }];
  const days = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];
  const months = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
  for (let i = 0; i < 7; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    out.push({ v: iso, l: i === 0 ? "Bugün" : i === 1 ? "Yarın" : `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}` });
  }
  return out;
}

export default function Explore() {
  const [sp, setSp] = useSearchParams();
  const [view, setView] = useState("studios");
  const dates = useMemo(buildDates, []);

  const category = sp.get("category") || "";
  const region = sp.get("region") || "";
  const search = sp.get("search") || "";
  const date = sp.get("date") || "";
  const afterTime = sp.get("after_time") || "";
  const minCredit = sp.get("min_credit") || "";
  const maxCredit = sp.get("max_credit") || "";

  const setParam = (k, v) => {
    const n = new URLSearchParams(sp);
    if (v) n.set(k, v); else n.delete(k);
    setSp(n);
  };

  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: () => get("/categories") });

  const studioParams = { category, region, search, min_credit: minCredit || undefined, max_credit: maxCredit || undefined };
  const { data: studios = [], isLoading: sl } = useQuery({
    queryKey: ["studios", studioParams],
    queryFn: () => get("/studios", studioParams),
    enabled: view === "studios",
  });

  const sessionParams = { category, region, date, after_time: afterTime, min_credit: minCredit || undefined, max_credit: maxCredit || undefined };
  const { data: sessions = [], isLoading: sesL } = useQuery({
    queryKey: ["sessions", sessionParams],
    queryFn: () => get("/sessions", sessionParams),
    enabled: view === "sessions",
  });

  const selectCls = "w-full rounded-xl border border-[#E5E0D8] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C6A5D]";

  return (
    <div className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
      <h1 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">Keşfet</h1>
      <p className="mt-2 text-[#5C5856]">Stüdyoları ve seansları filtrele, sana uygun olanı bul.</p>

      {/* Category pills */}
      <div className="mt-6 flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
        <button
          data-testid="filter-category-all"
          onClick={() => setParam("category", "")}
          className={`shrink-0 rounded-full border px-4 py-2 text-sm transition-colors ${!category ? "border-[#8C6A5D] bg-[#8C6A5D] text-white" : "border-[#E5E0D8] bg-white text-[#5C5856]"}`}
        >
          Tümü
        </button>
        {categories.map((c) => {
          const Icon = CATEGORY_ICONS[c.slug];
          const active = category === c.slug;
          return (
            <button
              key={c.slug}
              data-testid={`filter-category-${c.slug}`}
              onClick={() => setParam("category", active ? "" : c.slug)}
              className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors ${active ? "border-[#8C6A5D] bg-[#8C6A5D] text-white" : "border-[#E5E0D8] bg-white text-[#5C5856] hover:border-[#8C6A5D]"}`}
            >
              {Icon && <Icon className="h-4 w-4" />} {c.name}
            </button>
          );
        })}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[280px_1fr]">
        {/* Filters */}
        <aside className="h-fit rounded-2xl border border-[#E5E0D8] bg-white p-5">
          <div className="mb-4 flex items-center gap-2 font-medium">
            <SlidersHorizontal className="h-4 w-4 text-[#8C6A5D]" /> Filtreler
          </div>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm text-[#5C5856]">Bölge</label>
              <select data-testid="filter-region" value={region} onChange={(e) => setParam("region", e.target.value)} className={selectCls}>
                <option value="">Tüm bölgeler</option>
                {REGIONS.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
            {view === "sessions" && (
              <>
                <div>
                  <label className="mb-1.5 block text-sm text-[#5C5856]">Tarih</label>
                  <select data-testid="filter-date" value={date} onChange={(e) => setParam("date", e.target.value)} className={selectCls}>
                    {dates.map((d) => <option key={d.v} value={d.v}>{d.l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm text-[#5C5856]">Saat</label>
                  <select data-testid="filter-time" value={afterTime} onChange={(e) => setParam("after_time", e.target.value)} className={selectCls}>
                    {TIMES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                  </select>
                </div>
              </>
            )}
            <div>
              <label className="mb-1.5 block text-sm text-[#5C5856]">Kredi aralığı</label>
              <div className="flex items-center gap-2">
                <input data-testid="filter-min-credit" type="number" min="0" placeholder="Min" value={minCredit} onChange={(e) => setParam("min_credit", e.target.value)} className={selectCls} />
                <span className="text-[#C9BFB1]">–</span>
                <input data-testid="filter-max-credit" type="number" min="0" placeholder="Max" value={maxCredit} onChange={(e) => setParam("max_credit", e.target.value)} className={selectCls} />
              </div>
            </div>
            <button
              data-testid="clear-filters-button"
              onClick={() => setSp(new URLSearchParams())}
              className="w-full rounded-xl border border-[#E5E0D8] py-2.5 text-sm text-[#5C5856] transition-colors hover:bg-[#F4EFE7]"
            >
              Filtreleri temizle
            </button>
          </div>
        </aside>

        {/* Results */}
        <div>
          <div className="mb-5 inline-flex rounded-full border border-[#E5E0D8] bg-white p-1">
            <button data-testid="view-studios" onClick={() => setView("studios")} className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${view === "studios" ? "bg-[#2D2926] text-white" : "text-[#5C5856]"}`}>Stüdyolar</button>
            <button data-testid="view-sessions" onClick={() => setView("sessions")} className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${view === "sessions" ? "bg-[#2D2926] text-white" : "text-[#5C5856]"}`}>Seanslar</button>
          </div>

          {view === "studios" ? (
            sl ? <p className="text-[#5C5856]">Yükleniyor…</p> : studios.length === 0 ? (
              <p data-testid="no-results" className="rounded-2xl border border-dashed border-[#E5E0D8] p-10 text-center text-[#5C5856]">Sonuç bulunamadı. Filtreleri değiştirin.</p>
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="studios-grid">
                {studios.map((s, i) => <StudioCard key={s.id} studio={s} index={i} />)}
              </div>
            )
          ) : (
            sesL ? <p className="text-[#5C5856]">Yükleniyor…</p> : sessions.length === 0 ? (
              <p data-testid="no-results" className="rounded-2xl border border-dashed border-[#E5E0D8] p-10 text-center text-[#5C5856]">Seans bulunamadı. Filtreleri değiştirin.</p>
            ) : (
              <div className="grid gap-5 md:grid-cols-2" data-testid="sessions-grid">
                {sessions.map((s, i) => <SessionCard key={s.id} session={s} index={i} />)}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
